<script lang="ts" setup>
const props = defineProps({
  companies: {
    default: undefined,
    type: Array as PropType<CompanyV2[]>,
  },
  company: {
    default: undefined,
    type: Object as PropType<CompanyV2>,
  },
  size: {
    default: 'md',
    type: String as PropType<UInputSize>,
  },
})

const emit = defineEmits<{
  toggleCompanies: [{ action: 'attach' | 'detach', companies: CompanyV2[], listId: number }]
  updated: []
}>()

const { t } = useI18n()
const { createToast } = useToasts()
const listsStore = useListsStore()

const editing = ref(false)
const selectMenuRef = ref()
const updatingList = ref(false)

const options = computed((): USelectMenuOption[] => {
  return (listsStore.lists || []).map(list => ({
    count: list?.analytics?.companiesCount,
    id: list.id,
    label: list.name,
    selected: selectedListsIds.value.includes(list.id),
  }))
})

const selectedListsIds = computed(() => {
  if (props.companies) {
    return []
  }

  return (props?.company?.meta?.listIds || []).map(listId => listId)
})

function clearInput() {
  if (import.meta.client) {
    try {
      selectMenuRef.value.onQueryChange({ target: { value: '' } })
    }
    catch {}
  }
}

async function onChange(value: string | USelectMenuOption) {
  const companies = props.companies || (props.company ? [props.company] : [])

  updatingList.value = true

  try {
    let option: USelectMenuOption

    // Create a new list as the option is the input value
    if (typeof value === 'string') {
      const newList = await listsStore.createList({ name: value })

      option = {
        id: newList.id,
        label: value,
      }
    }
    else {
      option = value
    }

    const isAlreadyAttached = companies.every((company) => {
      return company.meta?.listIds?.includes(option.id)
    })

    if (isAlreadyAttached) {
      // Detach the company from the list
      await listsStore.toggleCompaniesFromList({
        action: 'detach',
        companies,
        listId: option.id,
        onToggleCompanies: (params) => {
          emit('toggleCompanies', params)
        },
      })

      createToast({
        i18nParams: { count: companies.length },
        message: 'listCompanyRemoved',
      })
    }
    else {
      // Create a new list as the option is the input value
      if (!option.id && typeof option.label === 'string') {
        const newList = await listsStore.createList({ name: option.label })

        option.id = newList.id
      }

      await listsStore.toggleCompaniesFromList({
        action: 'attach',
        companies,
        listId: option.id,
        onToggleCompanies: (params) => {
          emit('toggleCompanies', params)
        },
      })

      createToast({
        i18nParams: { count: companies.length },
        message: 'listCompanyAdded',
      })
    }

    emit('updated')
  }
  catch {
    createToast({
      message: 'somethingWentWrong',
      status: 'error',
    })
  }

  clearInput()

  updatingList.value = false
}
</script>

<template>
  <USelectMenu
    v-if="!editing"
    ref="selectMenuRef"
    creatable
    :icon="getIcon('lists')"
    :loading="updatingList"
    :options="options"
    :popper="{ strategy: 'absolute' }"
    :required="false"
    searchable
    :searchable-placeholder="listsStore.lists.length ? t('placeholders.searchOrCreate') : t('placeholders.create')"
    :size="size"
    :ui-menu="{
      container: 'min-w-48',
      empty: 'w-full overflow-x-auto',
      option: {
        base: 'w-full overflow-x-auto',
      },
    }"
    @change="onChange"
  >
    <template #label>
      <span
        v-if="Array.isArray(selectedListsIds)"
        class="truncate"
      >
        {{ selectedListsIds.length ? `${selectedListsIds.length} ${t('listAttached', selectedListsIds.length)}` : t('addCompanyToList') }}
      </span>
    </template>

    <template #option="{ option }">
      <div class="flex items-center gap-2 overflow-x-auto text-sm">
        <UIcon
          class="size-5 flex-shrink-0"
          :class="{ 'text-green-600 dark:text-green-400': option.selected }"
          :name="getIcon(option.selected ? 'check' : 'list')"
        />

        {{ option.label }}

        <UBadge
          v-if="typeof option.count === 'number' && option.count > 0"
          class="ml-1 flex items-center"
          :label="`${option.count} ${t('globals.company', option.count).toLowerCase()}`"
          size="xs"
          variant="subtle"
        />
      </div>
    </template>

    <template #option-create="{ option }">
      <div class="flex items-center gap-2">
        <UIcon
          class="size-5 flex-shrink-0"
          :name="getIcon('list')"
        />

        <span class="text-sm">
          {{ t('createList') }}: <span class="font-bold">{{ typeof option === 'string' ? option : option.label }}</span>
        </span>
      </div>
    </template>

    <template #empty>
      <div class="w-full overflow-x-auto">
        {{ t('typeSomething') }}
      </div>
    </template>
  </USelectMenu>
</template>

<i18n lang="json">
{
  "en": {
    "addCompanyToList": "Add this company to a list",
    "createList": "Create a new list",
    "listAttached": "list attached | lists attached",
    "placeholders": {
      "create": "Create a new list",
      "searchOrCreate": "Search or create a new list"
    },
    "typeSomething": "Type something to create a new list"
  },
  "es": {
    "addCompanyToList": "Añadir esta empresa a una lista",
    "createList": "Crear una nueva lista",
    "listAttached": "lista añadida | listas añadidas",
    "placeholders": {
      "create": "Crear una nueva lista",
      "searchOrCreate": "Buscar o crear una nueva lista"
    },
    "typeSomething": "Escribe algo para crear una nueva lista"
  },
  "fr": {
    "addCompanyToList": "Ajouter cette entreprise à une liste",
    "createList": "Créer un nouvelle liste",
    "listAttached": "liste attachée | listes attachées",
    "placeholders": {
      "create": "Créer une nouvelle liste",
      "searchOrCreate": "Rechercher ou créer une nouvelle liste"
    },
    "typeSomething": "Tapez quelque chose pour créer une nouvelle liste"
  }
}
</i18n>
